/**
 * useSearchBarHelpers - Custom hooks and helpers for SearchBar component
 * Extracted to reduce main component complexity and fix React Hook warnings
 */

import { useCallback, useEffect } from 'react';
import type { SearchResult } from '../../types/search';

interface SearchBarState {
  query: string;
  isLoading: boolean;
  isOpen: boolean;
  results: unknown[];
  suggestions: SearchResult[];
  error: string | null;
  selectedIndex: number;
}

/**
 * Custom hook for search functionality
 */
export const useSearchLogic = (
  onSearch: ((query: string) => Promise<unknown>) | undefined,
  cache: { get: (key: string) => unknown; set: (key: string, value: unknown) => void } | undefined,
  recentSearches: string[],
  maxSuggestions: number,
  showRecentSearches: boolean,
  safeSetState: (updater: (prev: SearchBarState) => SearchBarState) => void
) => {
  const performSearch = useCallback(async (query: string) => {
    // This will use the existing performSearchWithCache logic
    // but through the existing pattern in the main component
    if (!query.trim()) {
      safeSetState(prev => ({ 
        ...prev, 
        suggestions: [], 
        isLoading: false 
      }));
      return;
    }
    
    safeSetState(prev => ({ ...prev, isLoading: true }));
    
    try {
      if (onSearch) {
        const results = await onSearch(query);
        const suggestions = Array.isArray(results) 
          ? results 
          : (results && typeof results === 'object' && 'results' in results) 
            ? (results as { results: unknown[] }).results || []
            : [];
        safeSetState(prev => ({ 
          ...prev, 
          suggestions: suggestions.slice(0, maxSuggestions),
          isLoading: false,
          error: null
        }));
      }
    } catch {
      safeSetState(prev => ({ 
        ...prev, 
        error: 'Search failed',
        isLoading: false
      }));
    }
  }, [onSearch, maxSuggestions, safeSetState]);

  return { performSearch };
};

/**
 * Custom hook for input change handling
 */
export const useInputChangeHandler = (
  setState: (updater: (prev: SearchBarState) => SearchBarState) => void
) => {
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setState(prev => ({ 
        ...prev, 
        query: event.target.value, 
        isOpen: true, 
        selectedIndex: -1 
      }));
    },
    [setState]
  );

  return { handleInputChange };
};

/**
 * Custom hook for clear button handling
 */
export const useClearHandler = (
  handleKeyboardNavigation: (action: string) => void,
  inputRef: React.RefObject<HTMLInputElement>
) => {
  const handleClearClick = useCallback(() => {
    handleKeyboardNavigation('clear');
    inputRef.current?.focus();
  }, [handleKeyboardNavigation, inputRef]);

  return { handleClearClick };
};

/**
 * Custom hook for suggestion hover handling
 */
export const useSuggestionHoverHandler = (
  setState: (updater: (prev: SearchBarState) => SearchBarState) => void
) => {
  const handleSuggestionHover = useCallback((index: number) => {
    setState(prev => ({ ...prev, selectedIndex: index }));
  }, [setState]);

  return { handleSuggestionHover };
};

/**
 * Custom hook for auto-focus effect
 */
export const useAutoFocus = (
  autoFocus: boolean,
  inputRef: React.RefObject<HTMLInputElement>
) => {
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, inputRef]);
};